import * as React from 'react'

import { Box, Chevron, Popover, Text, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import i18n from 'modules/core/lib/i18n'
import {
  formatCounterAmount,
  isHourlyFixedBalanceAllowance,
} from 'modules/timeoff/components/Leaves/Overview/formatCounterAmount'

import Incidence from 'modules/timeoff/components/Leaves/Overview/LegacyStatsCounter/AvailableTooltip/Incidence'
import { useContractsVersion } from 'modules/timeoff/lib/contractsVersionContext'
import type Allowance from 'modules/timeoff/store/models/Allowance'
import type AllowanceIncidence from 'modules/timeoff/store/models/AllowanceIncidence'
import type { Cycle } from 'modules/timeoff/store/models/AllowanceStat'

import DaysPerPolicy from './DaysPerPolicy'
import ProratedDays from './ProratedDays'
import Total from './Total'

interface Props {
  allowance: Allowance
  policyAllowance: number
  proratedDays: number
  accruedUnits: number
  proratedDaysInDecimal: number
  accrualCycle: Cycle | null | undefined
  terminationDate?: string
  incidences: AllowanceIncidence[]
}

const AccruedTooltip = ({
  allowance,
  policyAllowance,
  proratedDays,
  proratedDaysInDecimal,
  accrualCycle,
  accruedUnits,
  terminationDate,
  incidences,
}: Props) => {
  const [open, toggle] = useToggle()
  const { startsOn, endsOn } = useContractsVersion()

  const isTimeWorked = allowance.isTimeWorked
  if (policyAllowance === null && !isTimeWorked) {
    return <Text.Header3>{i18n.t('time_off.unlimited')}</Text.Header3>
  }

  const terminatedSameYear = endsOn && accrualCycle ? accrualCycle.covers(endsOn) : false
  const hiredSameYear = startsOn && accrualCycle ? accrualCycle.covers(startsOn) : false

  const title = allowance.isDays
    ? i18n.t('time_off.accrued_tooltip.accrued_days')
    : i18n.t('time_off.accrued_tooltip.accrued_hours')

  const isHourlyFixedBalance = isHourlyFixedBalanceAllowance(allowance)

  const formattedAccruedUnits = formatCounterAmount(accruedUnits, isHourlyFixedBalance)
  const formattedProratedDays = formatCounterAmount(proratedDays, isHourlyFixedBalance)
  const formattedTotal = formatCounterAmount(accruedUnits, isHourlyFixedBalance)

  const policyOrAccruedAmount = !isTimeWorked ? policyAllowance : proratedDays

  return (
    <Popover.ItemList
      attachment="top center"
      targetAttachment="bottom center"
      toggle={toggle}
      open={open}
      title={title}
    >
      <Box flexDirection="row" alignItems="center">
        <Box alignItems="center" gap="s4">
          <Box flexDirection="row" alignItems="center" gap="s4">
            <Text.Header1>{formattedAccruedUnits}</Text.Header1>
            <Chevron direction={open ? 'up' : 'down'} type="quaternary" />
          </Box>
          <Text.Header3>{i18n.t('time_off.accrued_stats')}</Text.Header3>
        </Box>
      </Box>
      <DaysPerPolicy amount={policyOrAccruedAmount} allowance={allowance} />
      {Boolean(!isTimeWorked && (hiredSameYear || terminatedSameYear || terminationDate)) && (
        <ProratedDays
          terminationDate={terminationDate}
          hiredSameYear={hiredSameYear}
          terminatedSameYear={terminatedSameYear}
          proratedDays={formattedProratedDays}
          proratedDaysInDecimal={proratedDaysInDecimal}
          allowance={allowance}
        />
      )}
      {incidences.map((allowanceIncidence) => (
        <Incidence
          key={allowanceIncidence.attributes.id}
          allowanceIncidence={allowanceIncidence}
          isHourlyFixedBalance={isHourlyFixedBalance}
        />
      ))}
      {!isTimeWorked && (
        <Total
          amount={formattedTotal}
          accrualCycle={accrualCycle}
          accruedOnPrevCycle={allowance.isNextCycleAvailability}
        />
      )}
    </Popover.ItemList>
  )
}

export default observer(AccruedTooltip)
